// Price-rule engine shared by the TV display and the admin.
// Original prices are never modified: a rule only changes what is SHOWN while it is live,
// so when its time window ends the TV falls back to the original price by itself.

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// (Written without Object.fromEntries / hourCycle-only formatting so it also runs on older Fire TV browsers.)
function partsOf(opts, at) {
  const out = {};
  new Intl.DateTimeFormat('en-US', Object.assign({ hour12: false, hourCycle: 'h23' }, opts)).formatToParts(at)
    .forEach((p) => { out[p.type] = p.value; });
  if (out.hour === '24') out.hour = '00';   // some browsers write midnight as 24:00
  return out;
}
export const DAY_NAMES = DAYS;

/** Wall-clock parts of `at` in time zone `tz`. */
export function localNow(tz, at = new Date()) {
  const parts = partsOf({
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short',
  }, at);
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    dow: DAYS.indexOf(parts.weekday),
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
    instant: at,
    tz,
  };
}

/** "14:30" or "14:30:00" → 870; empty → null */
export function toMin(t) {
  if (!t) return null;
  const [h, m] = String(t).split(':');
  return Number(h) * 60 + Number(m);
}

export function isRuleLive(rule, now) {
  if (!rule.active) return false;
  const t = now.instant.getTime();
  if (rule.starts_at && t < Date.parse(rule.starts_at)) return false;
  if (rule.ends_at && t >= Date.parse(rule.ends_at)) return false;
  if (rule.start_date && now.date < rule.start_date) return false;
  if (rule.end_date && now.date > rule.end_date) return false;
  if (rule.days && rule.days.length && !rule.days.map(Number).includes(now.dow)) return false;
  const s = toMin(rule.start_time);
  const e = toMin(rule.end_time);
  if (s !== null || e !== null) {
    const S = s ?? 0;
    const E = e ?? 24 * 60;
    const inside = S <= E ? now.minutes >= S && now.minutes < E : now.minutes >= S || now.minutes < E;
    if (!inside) return false;
  }
  return true;
}

/** 'off' | 'live' | 'expired' | 'scheduled' */
export function ruleStatus(rule, now) {
  if (!rule.active) return 'off';
  if (isRuleLive(rule, now)) return 'live';
  if (rule.ends_at && now.instant.getTime() >= Date.parse(rule.ends_at)) return 'expired';
  if (rule.end_date && now.date > rule.end_date) return 'expired';
  return 'scheduled';
}

const round2 = (n) => Math.round(n * 100) / 100;

export function applyRule(price, rule) {
  const v = Number(rule.value);
  let out = price;
  if (rule.mode === 'set') out = v;
  else if (rule.mode === 'percent_off') out = price * (1 - v / 100);
  else if (rule.mode === 'amount_off') out = price - v;
  return Math.max(0, round2(out));
}

/** Price to show right now. If several specials apply, the lowest price wins. */
export function priceFor(item, rules, now) {
  const original = Number(item.price);
  let best = { price: original, original, rule: null };
  for (const rule of rules) {
    if (!rule.item_ids || !rule.item_ids.includes(item.id)) continue;
    if (!isRuleLive(rule, now)) continue;
    const p = applyRule(original, rule);
    if (p < best.price) best = { price: p, original, rule };
  }
  return best;
}

export function currentPeriod(restaurant, now) {
  return now.minutes < (toMin(restaurant.lunch_end) ?? 15 * 60) ? 'lunch' : 'dinner';
}

export function itemVisible(item, period) {
  return item.available && (item.meal === 'all' || item.meal === period);
}

// ── Formatting helpers ──

export function money(n, { cents = 'auto' } = {}) {
  const v = Number(n);
  if (cents === 'auto' && Number.isInteger(v)) return `$${v}`;
  return `$${v.toFixed(2)}`;
}

export function fmtTime(t) {
  const m = toMin(t);
  if (m === null) return '';
  const h = Math.floor(m / 60) % 24;
  const mm = String(m % 60).padStart(2, '0');
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 || 12;
  return mm === '00' ? `${h12} ${ampm}` : `${h12}:${mm} ${ampm}`;
}

/** Instant → "3:45 PM" in tz */
export function fmtInstantTime(iso, tz) {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' })
    .format(new Date(iso)).replace(':00', '');
}

export function fmtInstant(iso, tz) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(new Date(iso));
}

export function fmtDate(d) {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
}

export function describeEffect(rule) {
  const v = Number(rule.value);
  if (rule.mode === 'set') return `now ${money(v, { cents: 'always' })}`;
  if (rule.mode === 'percent_off') return `${v}% off`;
  return `${money(v)} off`;
}

export function describeWhen(rule, tz) {
  const bits = [];
  if (rule.starts_at || rule.ends_at) {
    if (rule.starts_at) bits.push(`from ${fmtInstant(rule.starts_at, tz)}`);
    if (rule.ends_at) bits.push(`until ${fmtInstant(rule.ends_at, tz)}`);
  }
  if (rule.days && rule.days.length && rule.days.length < 7) {
    bits.push(rule.days.map((d) => DAYS[d]).join(', '));
  } else if (!rule.starts_at && !rule.ends_at && !(rule.start_date && rule.start_date === rule.end_date)) {
    bits.push('Every day');
  }
  if (rule.start_time || rule.end_time) {
    bits.push(`${fmtTime(rule.start_time || '00:00')}–${rule.end_time ? fmtTime(rule.end_time) : 'close'}`);
  }
  if (rule.start_date && rule.start_date === rule.end_date) bits.push(`on ${fmtDate(rule.start_date)}`);
  else {
    if (rule.start_date) bits.push(`from ${fmtDate(rule.start_date)}`);
    if (rule.end_date) bits.push(`through ${fmtDate(rule.end_date)}`);
  }
  return bits.join(' · ');
}

/** When does a live rule stop today? Used for "until 5 PM" labels on the TV. */
export function liveUntilLabel(rule, now) {
  if (rule.ends_at) {
    const end = new Date(rule.ends_at);
    const sameDay = localNow(now.tz, end).date === now.date;
    if (sameDay) return `until ${fmtInstantTime(rule.ends_at, now.tz)}`;
  }
  if (rule.end_time) return `until ${fmtTime(rule.end_time)}`;
  if (rule.end_date === now.date) return 'today only';
  return '';
}

// ── Time-zone conversions for <input type="datetime-local"> ──

function tzOffsetMs(tz, utcMs) {
  const p = partsOf({
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }, new Date(utcMs));
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return asUtc - utcMs;
}

/** "2026-10-05T14:30" understood as wall time in tz → ISO instant */
export function wallToIso(wall, tz) {
  if (!wall) return null;
  const [d, t] = wall.split('T');
  const [y, mo, da] = d.split('-').map(Number);
  const [h, mi] = t.split(':').map(Number);
  const guess = Date.UTC(y, mo - 1, da, h, mi);
  let ms = guess - tzOffsetMs(tz, guess);
  ms = guess - tzOffsetMs(tz, ms); // second pass handles DST edges
  return new Date(ms).toISOString();
}

/** ISO instant → "2026-10-05T14:30" wall time in tz */
export function isoToWall(iso, tz) {
  if (!iso) return '';
  const n = localNow(tz, new Date(iso));
  const h = String(Math.floor(n.minutes / 60)).padStart(2, '0');
  const m = String(n.minutes % 60).padStart(2, '0');
  return `${n.date}T${h}:${m}`;
}
